import React, { useEffect, useState } from "react";
import {
  Paper,
  Typography,
  Button,
  Box,
  TextField,
  Tabs,
  Tab,
  Grid2,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { getDeviceList } from "../../redux/reducers/deviceReducer";
import CustomGenericTable from "../../components/CustomGenericTable";
import Loader from "../../components/Loader";
import withToast from "../../hocs/withToast";
import moment from "moment";
import withNavigate from "../../hocs/withNavigate";

const columns = [
  { id: "deviceSerialNumber", label: "Serial" },
  { id: "productModel", label: "Model" },
  { id: "firmwareVersion", label: "Firmware" },
  { id: "statusType", label: "Status" },
  { id: "patientName", label: "Patient Name" },
  { id: "assignmentDate", label: "Assignment Date", formatDate: true },
  { id: "lastTimeActivity", label: "Last Activity", formatDate: true },
];

const DeviceManagement = ({ navigate }) => {
  const dispatch = useDispatch();
  const { deviceList = [], loading, rowCount } = useSelector((state) => state.device);
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredDevices, setFilteredDevices] = useState(deviceList || []);

  useEffect(() => {
    dispatch(getDeviceList());
  }, [dispatch]);

  useEffect(() => {
    setFilteredDevices(deviceList);
  }, [deviceList])

  useEffect(() => {
    let filteredList = deviceList;
    if (selectedTab === 1)
      filteredList = deviceList.filter((device) => device.assignmentDate);
    else if (selectedTab === 2)
      filteredList = deviceList.filter((device) => !device.assignmentDate);

    if (searchQuery) {
      filteredList = filteredList.filter((device) =>
        device.deviceSerialNumber.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    setFilteredDevices(filteredList);
  }, [selectedTab, searchQuery, deviceList]);

  const handlePatientClick = (patient) => {
    navigate("/patient-chart", {
      state: { patientId: patient.patientId },
    });
  };

  const rowsWithAction = (rows) => {
    return rows?.length && rows.map((e) => ({
      ...e,
      patientName: e.patientFirstName ? `${e.patientFirstName} ${e.patientLastName}` : '',
      assignmentDate: e.assignmentDate ? moment(e.assignmentDate).format('DD-MM-YYYY') : '',
      lastTimeActivity: e.lastTimeActivity ? moment(e.lastTimeActivity).format('MMMM Do YYYY, h:mm:ss a') : ''
    }));
  };

  const assignedDevice = deviceList?.filter((ele) => ele.isAssigned).length;
  const unassignedDevice = (deviceList?.length || 0) - (deviceList?.filter((ele) => ele.isAssigned)?.length || 0);

  return (
    <>
      {/* Header Section */}
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 2 }}>
        Device Management
      </Typography>

      {/* Device Count Section */}
      <Paper elevation={5} sx={{ display: "flex", gap: "20px", p: 1, bgcolor: "white" }}>
        <Box sx={{ textAlign: "left", padding: "0px 40px" }}>
          <Typography variant="subtitle1">Total Devices</Typography>
          <Typography variant="h5" fontWeight="bold">{deviceList?.length}</Typography>
        </Box>
        <Box sx={{ textAlign: "left", padding: "0px 40px" }}>
          <Typography variant="subtitle1">Assigned Devices</Typography>
          <Typography variant="h5" fontWeight="bold">
            {assignedDevice}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "left", padding: "0px 40px" }}>
          <Typography variant="subtitle1">Unassigned Devices</Typography>
          <Typography variant="h5" fontWeight="bold">
            {unassignedDevice}
          </Typography>
        </Box>
      </Paper>

      {/* Tabs Section */}
      <Box sx={{ bgcolor: "#f5f5f5", mt: 3, border: "1px solid #ddd", borderRadius: "10px" }}>
        <Grid2
          container
          spacing={2}
          alignItems="center"
          sx={{ my: 0, borderRadius: "10px", p: 2, bgcolor: "white", marginBottom: '10px' }}
        >
          {/* Tabs for selecting device status */}
          <Tabs
            value={selectedTab}
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{ style: { display: "none" } }}
            onChange={(e, newValue) => setSelectedTab(newValue)}
          >
            <Tab
              label="All"
              sx={{
                color: "#000",
                "&.Mui-selected": {
                  color: "#000",
                  backgroundColor: "#f0f0f0",
                  borderRadius: '10px'
                },
              }}
            />
            <Tab
              label="Active"
              sx={{
                color: "#000",
                "&.Mui-selected": {
                  color: "#000",
                  backgroundColor: "#f0f0f0",
                  borderRadius: '10px'
                },
              }}
            />
            <Tab
              label="Inactive"
              sx={{
                color: "#000",
                "&.Mui-selected": {
                  color: "#000",
                  backgroundColor: "#f0f0f0",
                  borderRadius: '10px'
                },
              }}
            />
          </Tabs>
          {/* Search field */}
          <Grid2
            item
            xs={12}
            md={6}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <TextField
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by Serial Number"
              variant="outlined"
              size="small"
              sx={{
                width: { xs: "100%", md: "600px" },
                maxWidth: "750px",
                flexGrow: 1,
                borderRadius: "50px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "50px",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton edge="start">
                      <Search sx={{ color: "#c1c5cd" }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid2>
        </Grid2>

        <CustomGenericTable
          title="Devices"
          columns={columns}
          rows={rowsWithAction(filteredDevices) || []}
          rowCount={selectedTab === 1 ? assignedDevice : selectedTab === 2 ? unassignedDevice : rowCount}
          handleNameClickCb={handlePatientClick}
        />
        <Loader loading={loading} />
      </Box>
    </>
  );
};

export default withToast(withNavigate(DeviceManagement));